import { memo, useEffect, useRef, useState } from 'react';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { Formik, Form as FormikForm, FormikProps, Field } from 'formik';
import * as Yup from 'yup';

import Verified from 'assets/images/verified.gif';
import infoIco from 'assets/images/i-circle.svg';

import useApi from 'shared/hooks/useApi';
import CustomerOnboardingHttp from 'pages/customer-onboarding/services/customer.service';
import DashboardHttp from '../services/profile.service';
import { userStore } from 'store/UserStore';
import OtpWithTimer from 'shared/common/otpWithTimer/otpWithTimer';
import { toast } from 'react-toastify';

type Props = {};

const CustomerAadhaar = (props: Props) => {
  // Refs
  const formikRef = useRef<FormikProps<any>>();

  // States
  const [isAadhaarChanged, setIsAadhaarChanged] = useState<boolean>(false);
  const [isAadhaarVerified, setisAadhaarVerified] = useState<boolean>(false);
  const [aadhaarValue, setAadhaarValue] = useState<string>('');
  const [otpValue, setOtpValue] = useState<string>('');
  const [isOtpSent, setIsOtpSent] = useState<boolean>(false);
  const [aadhaar, setAadhaar] = useState<string>('');
  const [customer, customerId, setCustomer, user] = userStore((state: any) => [
    state.customer,
    state.customerId,
    state.setCustomer,
    state.user,
  ]);

  // Custom Hooks
  let aadhaarRequestOtpApi = useApi(CustomerOnboardingHttp.sendAadhaarOtp);
  let aadhaarVerifyOtpApi = useApi(CustomerOnboardingHttp.verifyAadhaarOtp);
  let getCustomerProfileApi = useApi(DashboardHttp.getProfile);

  // Effects
  useEffect(() => {
    if (customer?.kyc?.aadhaar?.isVerified)
      setAadhaar(customer?.kyc?.aadhaar?.value);
  }, [customer]);

  useEffect(() => {
    if (aadhaarVerifyOtpApi.responseCode === 200 && aadhaarVerifyOtpApi.data) {
      setIsOtpSent(false);
      setisAadhaarVerified(true);
      setIsAadhaarChanged(false);
      getCustomerProfileApi.request(customerId,user?.userId);
    }
  }, [aadhaarVerifyOtpApi.data]);

  useEffect(() => {
    setisAadhaarVerified(false);
  }, [aadhaarVerifyOtpApi.error]);

  useEffect(() => {
    if (
      aadhaarRequestOtpApi.responseCode === 200 &&
      aadhaarRequestOtpApi.data
    ) {
      setIsOtpSent(true);
      setIsAadhaarChanged(false);
      formikRef.current.setFieldError('aadhaar_no', '');
    }
  }, [aadhaarRequestOtpApi.data]);

  useEffect(() => {
    if (aadhaarRequestOtpApi.error) {
      formikRef.current.setFieldError(
        'aadhaar_no',
        'Error while verifying Aadhaar details'
      );
    }
  }, [aadhaarRequestOtpApi.error]);

  useEffect(() => {
    if (
      getCustomerProfileApi.responseCode === 200 &&
      getCustomerProfileApi.data.data
    ) {
      setCustomer(getCustomerProfileApi.data.data.customer);
    } else if (getCustomerProfileApi.error) {
      toast.error(getCustomerProfileApi.error.errors[0].title);
    }
  }, [getCustomerProfileApi.data, getCustomerProfileApi.error]);

  const verifyAadhaarOtp = async () => {
    let otp = formikRef.current.values?.aadhaar_otp;
    let aadhaarNo = formikRef.current.values?.aadhaar_no;
    if (otp?.length === 6 && /[0-9]{6}/.test(otp)) {
      let payload = {
        aadhaarNo,
        otp,
        userId: user?.userId,
      };
          aadhaarVerifyOtpApi.request(payload);
       if(aadhaarVerifyOtpApi.error.errors[0].title)
        {
          toast.error("Please enter valid OTP");
        }
    } else {
      formikRef.current.setFieldError('aadhaar_otp', 'Invalid Aadhaar Number');
    }
  };
  const sendOtp = async () => {
    let aadhaar_agree = formikRef.current.values?.aadhaar_agree;
    let aadhaar_no = formikRef.current.values?.aadhaar_no;
    if (!aadhaar_agree) {
      formikRef.current.setFieldError(
        'aadhaar_agree',
        'Please select consent first'
      );
      return;
    }
    if (aadhaar_no?.length === 12 && /[0-9]{12}/.test(aadhaar_no)) {
      let payload = {
        name: customer?.displayName,
        aadhaarNo: aadhaar_no,
        consent: true,
        user_id: user?.userId,
      };
      aadhaarRequestOtpApi.request(payload);
    } else {
      formikRef.current.setFieldError('aadhaar_no', 'Invalid Aadhaar Number');
    }
  };

  return aadhaar ? (
    <>
      <Form.Label>Aadhaar Number</Form.Label>
      <InputGroup className='mb-3'>
        <Form.Control
          placeholder='ABCDE1234F'
          aria-label='ABCDE1234F'
          aria-describedby='basic-addon2'
          name='disabled_pan'
          id='disabled_pan'
          value={aadhaar}
          disabled
        />

        <div className='input-group-text px-2'>
          <img src={Verified} width='22px' height='22px' />
        </div>
      </InputGroup>
    </>
  ) : (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={{
          aadhaar_no: '',
          aadhaar_otp: '',
          aadhaar_agree: false,
        }}
        onSubmit={() => {
          verifyAadhaarOtp();
        }}
        validationSchema={Yup.object().shape({
          aadhaar_agree: Yup.boolean(),
          aadhaar_otp: Yup.string().matches(
            /[0-9]{6}/,
            'Please enter valid OTP'
          ),
        })}
      >
        {({
          values,
          errors,
          touched,
          setFieldError,
          setFieldValue,
          setFieldTouched,
        }) => (
          <FormikForm>
            {!customer?.kyc?.aadhaar?.isVerified && (
              <>
                <Form.Group className='mb-3' controlId='kycForm.aadharInput'>
                  <Form.Label>Aadhaar Number</Form.Label>

                  <small className='text-muted'> (Optional)</small>
                  <InputGroup className='mb-3'>
                    <Form.Control
                      aria-describedby='basic-addon2'
                      name='aadhaar_no'
                      disabled={isAadhaarVerified}
                      value={aadhaarValue}
                      onBlur={(e) => {
                        setFieldTouched('aadhaar_no');
                      }}
                      onChange={(e) => {
                        setFieldValue(
                          'aadhaar_no',
                          e.target.value?.toUpperCase()
                        );
                        setAadhaarValue(e.target.value?.toUpperCase());
                        setIsAadhaarChanged(true);
                      }}
                    />
                    {aadhaarRequestOtpApi.loading ? (
                      <div className='input-group-text px-2'>
                        <Spinner animation='border' size='sm' />
                      </div>
                    ) : (
                      !errors.aadhaar_no &&
                      isAadhaarVerified &&
                      !isAadhaarChanged && (
                        <div className='input-group-text px-2'>
                          <img src={Verified} width='22px' height='22px' />
                        </div>
                      )
                    )}
                    {isAadhaarChanged && !aadhaarRequestOtpApi.loading && (
                      <button
                        className='btn btn-verify'
                        id='button-addon2'
                        type='button'
                        onClick={(e) => {
                          sendOtp();
                        }}
                      >
                        Request OTP
                      </button>
                    )}
                  </InputGroup>
                  <div className='error'>
                    {errors.aadhaar_no && touched.aadhaar_no && (
                      <small id='my-helper-text'>
                        <>
                          <img src={infoIco} alt='' />
                          {errors.aadhaar_no}
                        </>
                      </small>
                    )}
                  </div>
                </Form.Group>

                {values?.aadhaar_no?.length > 0 && !isAadhaarVerified && (
                  <>
                    <div className='form-group mb-2'>
                      <label className='form-label'>
                        <Field
                          type='checkbox'
                          placeholder=''
                          name='aadhaar_agree'
                          disabled={isOtpSent}
                          className='mx-1'
                          onChange={(e) => {
                            setFieldValue('aadhaar_agree', e.target.checked);
                            if (e.target.checked) {
                              setFieldError('aadhaar_agree', '');
                            }
                          }}
                        />
                        I Agree to the{' '}
                        <a
                          href='https://ebullion.in/terms-and-conditions/T&C.html'
                          target='_blank'
                        >
                          terms and conditions
                        </a>
                      </label>
                    </div>
                    <div className='form-group mb-4'>
                      <div className='error'>
                        {errors.aadhaar_agree && (
                          <small id='my-helper-text'>
                            <>
                              <img src={infoIco} alt='' />
                              {errors.aadhaar_agree}
                            </>
                          </small>
                        )}
                      </div>
                    </div>
                  </>
                )}
                {isOtpSent && (
                  <OtpWithTimer
                    enableResendTimer={true}
                    resendInterval={30}
                    numInputs={6}
                    otpLabel='Please enter OTP'
                    otpChangeHandler={(otp) => {
                      setFieldValue('aadhaar_otp', otp);
                      setOtpValue(otp);

                      if (otp?.length === 6) {
                        verifyAadhaarOtp();
                      }
                    }}
                    otpValue={otpValue}
                    isCentered
                    ackMessageType='success'
                    resendButtonText='Resend OTP'
                    ackMessage='OTP has been sent to your registered mobile number.'
                    resendHandler={() => sendOtp()}
                  ></OtpWithTimer>
                )}
              </>
            )}
            {isOtpSent ? (
              <div className='form-group my-3 mt-5'>
                <Button
                  disabled={
                    aadhaarRequestOtpApi.loading ||
                    aadhaarVerifyOtpApi.loading ||
                    getCustomerProfileApi.loading
                  }
                  variant='primary'
                  className='w-100'
                  size='lg'
                  type='submit'
                >
                  Verify OTP{' '}
                  {aadhaarRequestOtpApi.loading ||
                  aadhaarVerifyOtpApi.loading ||
                  getCustomerProfileApi.loading ? (
                    <Spinner animation='border' size='sm' />
                  ) : null}
                </Button>
              </div>
            ) : null}
          </FormikForm>
        )}
      </Formik>
    </>
  );
};

export default memo(CustomerAadhaar);
